import { createContext, useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { AttendanceResponseData, SessionAttendanceSummary } from '../components/attendance/types';
import { ClassMetrics, MetricTranslations, SessionMetrics, SessionSummary } from '../components/dashboard/types';
import { ADMIN_SETTINGS, CLASS_ATTENDANCE, CLASS_INFO, CLASS_METRICS, CLASS_SESSIONS, CLASS_STUDENTS } from '../config/apiPaths';
import { DEFAULT_SCHOOL_SETTINGS, SETTINGS_API } from '../config/settingsConfig';
import { GetAdminSettings, SchoolSettings } from '../types/apiTypes';
import { getUrls } from '../utils/url';
import { useDemoData } from './demoData/useDemoData';
import { ClassInfo, ClassSession, ClassStudent } from './types';

interface DashboardContextInterface {
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    openErrorModal: boolean;
    setOpenErrorModal: (openErrorModal: boolean) => void;
    errorMessage: string;
    sessions: ClassSession[];
    students: ClassStudent[];
    attendance: SessionAttendanceSummary | undefined;
    selectedSessionUuid: string | null;
    setSelectedSessionUuid: (selectedSessionUuid: string) => void;
    classMetrics: ClassMetrics | undefined;
    classAttendance?: SessionAttendanceSummary[];
    sessionMetrics: any;
    setSessionMetrics: (sessionMetrics: any) => void;
    sessionSummary: any;
    setSessionSummary: (sessionSummary: any) => void;
    classSettings: SchoolSettings | undefined;
    metricTranslations: MetricTranslations;
    view: View;
    setView: React.Dispatch<React.SetStateAction<View>>;
    classInfo: ClassInfo;
    classUuid: string;
    schoolSettings: SchoolSettings;
    fetchSessions: () => void;
}

export interface DashboardProviderProps {
    sessionUuid: string | null;
    classUuid: string;
    isDemo: string | null;
}

export type View = 'SessionSummary' | 'StudentDetail';

const DashboardContext = createContext(undefined as unknown as DashboardContextInterface);

const { Provider, Consumer } = DashboardContext;

const DashboardProvider: React.FC<DashboardProviderProps> = (props) => {
    const { sessionUuid, classUuid, isDemo: isDemoString, children } = props;
    const isDemo = isDemoString === 'true';

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [view, setView] = useState<View>('SessionSummary');

    const [sessions, setSessions] = useState<ClassSession[]>([]);
    const [students, setStudents] = useState<ClassStudent[]>([]);
    const [attendance, setAttendance] = useState<SessionAttendanceSummary | undefined>(undefined);
    const [classInfo, setClassInfo] = useState({ class_name: '', school_name: '', meeting_id: '', sid: 0 });
    const [schoolSettings, setSchoolSettings] = useState<SchoolSettings>(DEFAULT_SCHOOL_SETTINGS);

    const [selectedSessionUuid, setSelectedSessionUuid] = useState<string | null>(sessionUuid);
    const [classMetrics, setClassMetrics] = useState<ClassMetrics | undefined>(undefined);
    const [classAttendance, setClassAttendance] = useState<SessionAttendanceSummary[] | undefined>(undefined);
    const [sessionMetrics, setSessionMetrics] = useState<SessionMetrics | undefined>(undefined);
    const [sessionSummary, setSessionSummary] = useState<SessionSummary | undefined>(undefined);
    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [classSettings, setClassSettings] = useState<SchoolSettings | undefined>(undefined);
    const { api } = getUrls();
    const { t } = useTranslation('dashboard');

    const metricTranslations = {
        sec: t('sec'),
        secs: t('secs'),
        min: t('min'),
        mins: t('mins'),
        hr: t('hr'),
        hrs: t('hrs')
    };

    const removeFutureSessions = (classSessions) =>
        classSessions.filter((session) => {
            const now = new Date();
            const startTimeDate = new Date(session.start_time);
            return startTimeDate < now;
        });

    const sessionDateComparator = (a, b) => {
        const aDate = new Date(a.start_time);
        const bDate = new Date(b.start_time);
        if (aDate > bDate) return -1;
        if (aDate < bDate) return 1;
        return 0;
    };

    const fetchSessions = useCallback(async () => {
        try {
            const { data }: { data: ClassSession[] } = await axios.get(`${api}${CLASS_SESSIONS}?class_id=${classUuid}`);
            if (data.length < 1) {
                setIsLoading(false);
                return;
            }
            const availableSessions = removeFutureSessions(data);
            if ((!availableSessions || availableSessions.length === 0) && data.length > 0) {
                setErrorMessage(t('error.no-past-sessions'));
                setIsLoading(false);
                setOpenErrorModal(true);
            }
            const sortedSessions = availableSessions.sort(sessionDateComparator);
            setSessions(sortedSessions);
        } catch (err) {
            setErrorMessage(t('error.sessions'));
            setIsLoading(false);
            setOpenErrorModal(true);
        }
    }, [t, api, classUuid]);

    useEffect(() => {
        if (isDemo) return;

        const fetchStudents = async () => {
            try {
                const { data }: { data: ClassStudent[] } = await axios.get(`${api}${CLASS_STUDENTS}?class_id=${classUuid}`);
                if (!data || data.length === 0) {
                    setErrorMessage(t('error.no-students'));
                    setIsLoading(false);
                    setOpenErrorModal(true);
                }
                setStudents(data);
            } catch (err) {
                setErrorMessage(t('error.students'));
                setIsLoading(false);
                setOpenErrorModal(true);
            }
        };

        const fetchClassInfo = async () => {
            try {
                const { data }: { data: ClassInfo } = await axios.get(`${api}${CLASS_INFO}?class_id=${classUuid}`);
                setClassInfo(data);
            } catch (error) {
                return { className: '', schoolName: '' };
            }
        };

        const fetchSchoolSettings = async () => {
            try {
                const { data: body } = await axios.get<GetAdminSettings>(`${api}${SETTINGS_API}`);
                setSchoolSettings(body.data);
            } catch {
                return {};
            }
        };

        const fetchDashboard = async () => {
            await Promise.all([fetchSessions(), fetchStudents(), fetchClassInfo(), fetchSchoolSettings()]);
        };

        fetchDashboard();
    }, [api, classUuid, t, fetchSessions, isDemo]);

    useEffect(() => {
        if (!sessions.length || !students.length || isDemo) return;

        const fetchSessionMetrics = async () => {
            if (!selectedSessionUuid) {
                setSelectedSessionUuid(sessions[0].session_id);
                return;
            }
            try {
                const response = await axios.get(`${api}${CLASS_METRICS}/session?session_id=${selectedSessionUuid}`);
                setSessionMetrics(response.data);
            } catch (err) {
                setErrorMessage(t('error.metrics'));
                setIsLoading(false);
                setOpenErrorModal(true);
            }
        };
        const fetchAttendance = async () => {
            if (!selectedSessionUuid) return;
            try {
                const { data }: { data: AttendanceResponseData } = await axios.get(`${api}${CLASS_ATTENDANCE}/session?session_id=${selectedSessionUuid}`);

                const attendanceData: SessionAttendanceSummary = {
                    session_id: selectedSessionUuid,
                    students: data
                };

                setAttendance(attendanceData);
            } catch (err) {
                setErrorMessage(t('error.attendance'));
                setIsLoading(false);
                setOpenErrorModal(true);
            }
        };
        const fetchSessionData = async () => {
            setIsLoading(true);
            await Promise.all([fetchSessionMetrics(), fetchAttendance()]);
            setIsLoading(false);
        };

        fetchSessionData();
    }, [api, selectedSessionUuid, sessions, students, setSelectedSessionUuid, t, isDemo]);

    useEffect(() => {
        if (isDemo) return;

        const fetchClassMetrics = async () => {
            try {
                const response = await axios.get(`${api}${CLASS_METRICS}?class_id=${classUuid}`);
                setClassMetrics(response.data);
            } catch (err) {
                console.log(err);
                setErrorMessage(t('error.metrics'));
                setIsLoading(false);
                setOpenErrorModal(true);
            }
        };
        const fetchClassAttendance = async () => {
            try {
                const response = await axios.get(`${api}${CLASS_ATTENDANCE}?class_id=${classUuid}`);
                setClassAttendance(response.data);
            } catch (err) {
                console.log(err);
                setErrorMessage(t('error.attendance'));
                setIsLoading(false);
                setOpenErrorModal(true);
            }
        };
        const fetchClassSettings = async () => {
            try {
                const response = await axios.get(`${api}${ADMIN_SETTINGS}`);
                setClassSettings(response.data.data);
            } catch (err) {
                console.log(err);
                setErrorMessage(t('error.settings'));
                setIsLoading(false);
                setOpenErrorModal(true);
            }
        };

        const fetchAverageData = async () => {
            await Promise.all([fetchClassMetrics(), fetchClassAttendance(), fetchClassSettings()]);
        };

        fetchAverageData();
    }, [api, t, classUuid, isDemo]);

    useDemoData({
        isDemo,
        setStudents,
        setClassInfo,
        setSchoolSettings,
        setClassMetrics,
        setClassAttendance,
        setClassSettings,
        setSessions,
        sessions,
        selectedSessionUuid,
        setAttendance,
        isLoading,
        setIsLoading,
        setSessionMetrics
    });

    return (
        <Provider
            value={{
                isLoading,
                setIsLoading,
                openErrorModal,
                setOpenErrorModal,
                errorMessage,
                sessions,
                students,
                attendance,
                selectedSessionUuid,
                setSelectedSessionUuid,
                classMetrics,
                classAttendance,
                sessionMetrics,
                setSessionMetrics,
                sessionSummary,
                setSessionSummary,
                classSettings,
                metricTranslations,
                view,
                setView,
                classInfo,
                classUuid,
                fetchSessions,
                schoolSettings
            }}
        >
            {children}
        </Provider>
    );
};

export { DashboardContext, DashboardProvider, Consumer as DashboardConsumer };
